import { useContext, useEffect } from 'react'
import { AuthContext } from '@/context/auth-context'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { CircularProgressContinuous } from '@/components/loaders/CircularProgressContinuous'
import { InlineMessage } from '@/components/inline-message/InlineMessage'
import { Banner } from '@/components/banner/Banner'
import { Button } from '@/components/button/Button'
import { Tag } from '@/components/tag/Tag'
import { Calendar, Diamond } from '@/assets/icons'
import { AccountActions } from '@/account/store/slice'
import {
  selectActiveTrialEndingMessage,
  selectDisplayCostPerSeatInCents,
  selectHasForeverPercentDiscount,
  selectInTrialCanceledMessage,
} from '@/account/store/selectors'
import { StripeSubscriptionStatus } from '@/account/store/schemas'
import { SubscriptionStatus } from '@/firebase/auth/auth-jwt-schema'
import { closeSettingsModal, setReferralDialogVisible, setStripePortalDialogVisible } from '@/store/slices/ui-state.slice'
import { formatCentsToDollarsCompact } from '@/util/money'

import { SettingsPanelShell } from '../components/SettingsPanelShell'
import { SubscriptionDetailsCard } from '../components/SubscriptionDetailsCard'

/**
 * Subscription section (individual accounts): the Subscription Details card — the plan name, the
 * recurring price (discount-aware), Manage Subscription, and a Browse all plans link. Shares the card
 * with the org-admin BillingPanel. The account store has no fetch of its own, so this panel triggers
 * the payment-details refresh on mount (mirroring the legacy AccountPage).
 *
 * Price display mirrors the states in the Figma "Individual subscription use cases" and the behavior
 * of the replaced SubscriptionDetails:
 * - Lifetime % off: the struck standard price beside the discounted price, with an "X% OFF" badge.
 * - Other discounts: the standard price with a "Price shown before promotions" disclaimer.
 * - Exempt / no-billing accounts (Partner, Paxton, expired, …): their status label with no price,
 *   instead of mislabeling them as a priced "Individual plan".
 *
 * Referral-eligible accounts (non-anonymous, non-org, paid) also see the "Earn a free month" banner,
 * matching the Figma and the eligibility used by the sidebar account menu.
 *
 * Trialing accounts get a FREE TRIAL tag beside the title and a trial banner, reusing the variant
 * split from SubscriptionNoticeBanner: an active trial shows the blue renewal notice; a trial with
 * auto-renew turned off (cancelAtPeriodEnd) shows the yellow lose-access notice. The per-page trial
 * usage row from the Figma is deferred — it needs a backend usage endpoint that does not exist yet.
 */
export function SubscriptionPanel() {
  const dispatch = useAppDispatch()
  const { user, userAccountData } = useContext(AuthContext)
  const canViewSubscriptionInfo = userAccountData?.canViewStripeSubscriptionInfo
  const isReferralEligible = !user?.isAnonymous && !userAccountData?.isOrgAccount && userAccountData?.subscriptionStatus === SubscriptionStatus.PAID

  // Individuals bill a single seat, so the plan price is just the per-seat cost — no seat multiplier.
  // selectDisplayCostPerSeatInCents folds in a forever-percent discount; standardSeatCostCents is the
  // pre-discount price used for the struck-through original.
  const seatCostCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const standardSeatCostCents = useAppSelector((state: RootState) => state.accountState.costPerSeatInCents)
  const subscriptionInterval = useAppSelector((state: RootState) => state.accountState.stripeSubscriptionInterval)
  const hasForeverPercentDiscount = useAppSelector(selectHasForeverPercentDiscount)
  const hasDiscount = useAppSelector((state: RootState) => state.accountState.hasDiscount)
  const discountPercent = useAppSelector((state: RootState) => state.accountState.appliedCoupon?.percent_off)
  const isLoading = useAppSelector((state: RootState) => state.accountState.loading)
  const isInitialLoadComplete = useAppSelector((state: RootState) => state.accountState.initialLoadComplete)
  const paymentError = useAppSelector((state: RootState) => state.accountState.error)
  const showLoader = isLoading && !isInitialLoadComplete

  // Trial state, mirroring SubscriptionNoticeBanner: an active trial renews (blue notice); a trial
  // with auto-renew turned off will lose access (yellow notice).
  const stripeSubscriptionStatus = useAppSelector((state: RootState) => state.accountState.stripeSubscriptionStatus)
  const cancelAtPeriodEnd = useAppSelector((state: RootState) => state.accountState.cancelAtPeriodEnd)
  const activeTrialEndingMessage = useAppSelector(selectActiveTrialEndingMessage)
  const inTrialCanceledMessage = useAppSelector(selectInTrialCanceledMessage)
  const isInTrial = stripeSubscriptionStatus === StripeSubscriptionStatus.TRIALING
  const isTrialCanceled = isInTrial && cancelAtPeriodEnd

  const hasBillingInfo = seatCostCents != null && subscriptionInterval != null
  const planPrice = hasBillingInfo ? `${formatCentsToDollarsCompact(seatCostCents)} / ${subscriptionInterval}` : undefined

  // Lifetime % off: show the struck standard price + the "X% OFF" badge alongside the discounted price.
  const showForeverDiscount = hasBillingInfo && hasForeverPercentDiscount && standardSeatCostCents != null && discountPercent != null
  const originalPrice = showForeverDiscount ? `${formatCentsToDollarsCompact(standardSeatCostCents)} / ${subscriptionInterval}` : undefined
  const discountTag = showForeverDiscount ? `${discountPercent}% OFF` : undefined

  // Any other discount type is shown at the standard price with a disclaimer (the per-seat amount
  // charged can't be derived reliably), matching selectDisplayCostPerSeatInCents and the old component.
  const disclaimer = hasBillingInfo && hasDiscount && !hasForeverPercentDiscount ? 'Price shown before promotions' : undefined

  // Exempt / no-billing accounts have no Stripe price; show their real status rather than "Individual plan".
  const planName = hasBillingInfo ? 'Individual plan' : userAccountData?.subscriptionStatusDisplayName ?? 'Individual plan'

  useEffect(() => {
    if (canViewSubscriptionInfo) {
      dispatch(AccountActions.refreshPaymentDetails({ forceRefresh: true }))
    }
  }, [userAccountData, canViewSubscriptionInfo, dispatch])

  return (
    <SettingsPanelShell
      title="Subscription"
      titleAdornment={
        isInTrial ? (
          <Tag variant="tonal" color={isTrialCanceled ? 'Yellow' : 'Blue'}>
            Free trial
          </Tag>
        ) : undefined
      }
    >
      {showLoader ? (
        <div className="flex min-h-[40vh] items-center justify-center">
          <CircularProgressContinuous />
        </div>
      ) : paymentError ? (
        <InlineMessage variant="error">Could not load subscription details. Refresh the page and try again.</InlineMessage>
      ) : (
        <div className="flex flex-col gap-4">
          <SubscriptionDetailsCard
            planName={planName}
            planPrice={planPrice}
            originalPrice={originalPrice}
            discountTag={discountTag}
            disclaimer={disclaimer}
            onManageSubscription={canViewSubscriptionInfo ? () => dispatch(setStripePortalDialogVisible(true)) : undefined}
          />
          {isInTrial && (
            <Banner
              variant={isTrialCanceled ? 'warning' : 'info'}
              icon={<Calendar className="size-5" aria-hidden />}
              title={isTrialCanceled ? 'Free trial ending soon' : "You're on a free trial"}
              description={isTrialCanceled ? inTrialCanceledMessage : activeTrialEndingMessage}
            />
          )}
          {isReferralEligible && (
            <Banner
              variant="info"
              icon={<Diamond className="size-5" aria-hidden />}
              title="Earn a free month for each referral"
              action={
                <Button
                  variant="tonal"
                  color="primary"
                  size="xs"
                  onClick={() => {
                    dispatch(closeSettingsModal())
                    dispatch(setReferralDialogVisible(true))
                  }}
                >
                  Invite Someone
                </Button>
              }
            />
          )}
        </div>
      )}
    </SettingsPanelShell>
  )
}
